import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, merge, switchMap, take, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';

import { getSortedList, SortOrder } from '@common/utils/search-sort';
import { GeozonesService } from '@services/geozones.service';
import { loadMonitoringSuccess } from '@store/monitoring/monitoring.actions';
import { selectLastMessagesMonitoringObjects } from '@store/monitoring/monitoring.selectors';

import {
  getGeozones,
  setGeozonesLoading,
  storeGeozones,
  storeGeozonesObjectsCount,
  triggerManualLoadObjectsCount,
} from './geozones.actions';

@Injectable()
export class GeozonesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly geozonesService = inject(GeozonesService);

  getGeozones$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getGeozones),
      exhaustMap(() => {
        this.store.dispatch(setGeozonesLoading({ loading: true }));
        return this.geozonesService.getGeozonesList().pipe(
          map((geozones: any) => {
            this.store.dispatch(setGeozonesLoading({ loading: false }));
            return storeGeozones({ list: getSortedList(geozones, SortOrder.Asc) });
          }),
          catchError(() => {
            this.store.dispatch(setGeozonesLoading({ loading: false }));
            return EMPTY;
          }),
        );
      }),
    ),
  );

  autoLoadObjectsCount$ = createEffect(() =>
    merge(
      timer(0, 60000),
      this.actions$.pipe(ofType(triggerManualLoadObjectsCount)),
      this.actions$.pipe(
        ofType(loadMonitoringSuccess),
        filter(() => this.router.url.includes('geofences')),
      ),
    ).pipe(
      switchMap(() =>
        this.store.select(selectLastMessagesMonitoringObjects).pipe(
          take(1),
          map((objects) => {
            const currentRoute = this.router.url;
            if (!currentRoute.includes('geofences')) {
              return [];
            }

            return objects
              .map((m) => {
                if (!m?.lastMessage?.pos?.y || !m?.lastMessage?.pos?.x) return;
                return {
                  latitude: m?.lastMessage?.pos?.y,
                  longitude: m?.lastMessage?.pos?.x,
                };
              })
              .filter((f) => f);
          }),
          filter((formattedArray) => formattedArray.length > 0),
          switchMap((formattedArray) => {
            return this.geozonesService.sendObjectsData(formattedArray).pipe(
              map((response: any) => {
                return storeGeozonesObjectsCount({ geozonesObjectsCount: response });
              }),
              catchError(() => EMPTY),
            );
          }),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );
}
